import type { ErrorCorrectionLevel } from '../types.js';

export interface EcBlockGroup {
  /** Number of blocks with this shape. */
  count: number;
  /** Data codewords per block (EC codewords come on top). */
  dataCodewords: number;
}

export interface EcBlocks {
  ecCodewordsPerBlock: number;
  groups: EcBlockGroup[];
}

export interface VersionInfo {
  version: number;
  size: number;
  alignmentPositions: number[];
  ecBlocks: Record<ErrorCorrectionLevel, EcBlocks>;
}

export function versionForSize(size: number): number | null {
  if (size < 21 || size > 177 || (size - 17) % 4 !== 0) return null;
  return (size - 17) / 4;
}

export function sizeForVersion(version: number): number {
  return version * 4 + 17;
}

/** Total codewords (data + EC) in a version, derived from the EC table. */
export function totalCodewords(info: VersionInfo, level: ErrorCorrectionLevel): number {
  const { ecCodewordsPerBlock, groups } = info.ecBlocks[level];
  return groups.reduce((sum, g) => sum + g.count * (g.dataCodewords + ecCodewordsPerBlock), 0);
}

// Compact ECC table, one row per version, levels ordered L, M, Q, H.
// Row shape: [ecPerBlock, count1, data1, count2?, data2?] per level.
// Source: ISO/IEC 18004 error correction characteristics table.
type EcRow = [number, number, number] | [number, number, number, number, number];

const ECC_TABLE: [EcRow, EcRow, EcRow, EcRow][] = [
  /*  1 */ [
    [7, 1, 19],
    [10, 1, 16],
    [13, 1, 13],
    [17, 1, 9],
  ],
  /*  2 */ [
    [10, 1, 34],
    [16, 1, 28],
    [22, 1, 22],
    [28, 1, 16],
  ],
  /*  3 */ [
    [15, 1, 55],
    [26, 1, 44],
    [18, 2, 17],
    [22, 2, 13],
  ],
  /*  4 */ [
    [20, 1, 80],
    [18, 2, 32],
    [26, 2, 24],
    [16, 4, 9],
  ],
  /*  5 */ [
    [26, 1, 108],
    [24, 2, 43],
    [18, 2, 15, 2, 16],
    [22, 2, 11, 2, 12],
  ],
  /*  6 */ [
    [18, 2, 68],
    [16, 4, 27],
    [24, 4, 19],
    [28, 4, 15],
  ],
  /*  7 */ [
    [20, 2, 78],
    [18, 4, 31],
    [18, 2, 14, 4, 15],
    [26, 4, 13, 1, 14],
  ],
  /*  8 */ [
    [24, 2, 97],
    [22, 2, 38, 2, 39],
    [22, 4, 18, 2, 19],
    [26, 4, 14, 2, 15],
  ],
  /*  9 */ [
    [30, 2, 116],
    [22, 3, 36, 2, 37],
    [20, 4, 16, 4, 17],
    [24, 4, 12, 4, 13],
  ],
  /* 10 */ [
    [18, 2, 68, 2, 69],
    [26, 4, 43, 1, 44],
    [24, 6, 19, 2, 20],
    [28, 6, 15, 2, 16],
  ],
  /* 11 */ [
    [20, 4, 81],
    [30, 1, 50, 4, 51],
    [28, 4, 22, 4, 23],
    [24, 3, 12, 8, 13],
  ],
  /* 12 */ [
    [24, 2, 92, 2, 93],
    [22, 6, 36, 2, 37],
    [26, 4, 20, 6, 21],
    [28, 7, 14, 4, 15],
  ],
  /* 13 */ [
    [26, 4, 107],
    [22, 8, 37, 1, 38],
    [24, 8, 20, 4, 21],
    [22, 12, 11, 4, 12],
  ],
  /* 14 */ [
    [30, 3, 115, 1, 116],
    [24, 4, 40, 5, 41],
    [20, 11, 16, 5, 17],
    [24, 11, 12, 5, 13],
  ],
  /* 15 */ [
    [22, 5, 87, 1, 88],
    [24, 5, 41, 5, 42],
    [30, 5, 24, 7, 25],
    [24, 11, 12, 7, 13],
  ],
  /* 16 */ [
    [24, 5, 98, 1, 99],
    [28, 7, 45, 3, 46],
    [24, 15, 19, 2, 20],
    [30, 3, 15, 13, 16],
  ],
  /* 17 */ [
    [28, 1, 107, 5, 108],
    [28, 10, 46, 1, 47],
    [28, 1, 22, 15, 23],
    [28, 2, 14, 17, 15],
  ],
  /* 18 */ [
    [30, 5, 120, 1, 121],
    [26, 9, 43, 4, 44],
    [28, 17, 22, 1, 23],
    [28, 2, 14, 19, 15],
  ],
  /* 19 */ [
    [28, 3, 113, 4, 114],
    [26, 3, 44, 11, 45],
    [26, 17, 21, 4, 22],
    [26, 9, 13, 16, 14],
  ],
  /* 20 */ [
    [28, 3, 107, 5, 108],
    [26, 3, 41, 13, 42],
    [30, 15, 24, 5, 25],
    [28, 15, 15, 10, 16],
  ],
  /* 21 */ [
    [28, 4, 116, 4, 117],
    [26, 17, 42],
    [28, 17, 22, 6, 23],
    [30, 19, 16, 6, 17],
  ],
  /* 22 */ [
    [28, 2, 111, 7, 112],
    [28, 17, 46],
    [30, 7, 24, 16, 25],
    [24, 34, 13],
  ],
  /* 23 */ [
    [30, 4, 121, 5, 122],
    [28, 4, 47, 14, 48],
    [30, 11, 24, 14, 25],
    [30, 16, 15, 14, 16],
  ],
  /* 24 */ [
    [30, 6, 117, 4, 118],
    [28, 6, 45, 14, 46],
    [30, 11, 24, 16, 25],
    [30, 30, 16, 2, 17],
  ],
  /* 25 */ [
    [26, 8, 106, 4, 107],
    [28, 8, 47, 13, 48],
    [30, 7, 24, 22, 25],
    [30, 22, 15, 13, 16],
  ],
  /* 26 */ [
    [28, 10, 114, 2, 115],
    [28, 19, 46, 4, 47],
    [28, 28, 22, 6, 23],
    [30, 33, 16, 4, 17],
  ],
  /* 27 */ [
    [30, 8, 122, 4, 123],
    [28, 22, 45, 3, 46],
    [30, 8, 23, 26, 24],
    [30, 12, 15, 28, 16],
  ],
  /* 28 */ [
    [30, 3, 117, 10, 118],
    [28, 3, 45, 23, 46],
    [30, 4, 24, 31, 25],
    [30, 11, 15, 31, 16],
  ],
  /* 29 */ [
    [30, 7, 116, 7, 117],
    [28, 21, 45, 7, 46],
    [30, 1, 23, 37, 24],
    [30, 19, 15, 26, 16],
  ],
  /* 30 */ [
    [30, 5, 115, 10, 116],
    [28, 19, 47, 10, 48],
    [30, 15, 24, 25, 25],
    [30, 23, 15, 25, 16],
  ],
  /* 31 */ [
    [30, 13, 115, 3, 116],
    [28, 2, 46, 29, 47],
    [30, 42, 24, 1, 25],
    [30, 23, 15, 28, 16],
  ],
  /* 32 */ [
    [30, 17, 115],
    [28, 10, 46, 23, 47],
    [30, 10, 24, 35, 25],
    [30, 19, 15, 35, 16],
  ],
  /* 33 */ [
    [30, 17, 115, 1, 116],
    [28, 14, 46, 21, 47],
    [30, 29, 24, 19, 25],
    [30, 11, 15, 46, 16],
  ],
  /* 34 */ [
    [30, 13, 115, 6, 116],
    [28, 14, 46, 23, 47],
    [30, 44, 24, 7, 25],
    [30, 59, 16, 1, 17],
  ],
  /* 35 */ [
    [30, 12, 121, 7, 122],
    [28, 12, 47, 26, 48],
    [30, 39, 24, 14, 25],
    [30, 22, 15, 41, 16],
  ],
  /* 36 */ [
    [30, 6, 121, 14, 122],
    [28, 6, 47, 34, 48],
    [30, 46, 24, 10, 25],
    [30, 2, 15, 64, 16],
  ],
  /* 37 */ [
    [30, 17, 122, 4, 123],
    [28, 29, 46, 14, 47],
    [30, 49, 24, 10, 25],
    [30, 24, 15, 46, 16],
  ],
  /* 38 */ [
    [30, 4, 122, 18, 123],
    [28, 13, 46, 32, 47],
    [30, 48, 24, 14, 25],
    [30, 42, 15, 32, 16],
  ],
  /* 39 */ [
    [30, 20, 117, 4, 118],
    [28, 40, 47, 7, 48],
    [30, 43, 24, 22, 25],
    [30, 10, 15, 67, 16],
  ],
  /* 40 */ [
    [30, 19, 118, 6, 119],
    [28, 18, 47, 31, 48],
    [30, 34, 24, 34, 25],
    [30, 20, 15, 61, 16],
  ],
];

// Alignment pattern center coordinates per version (ISO/IEC 18004 Annex E).
const ALIGNMENT_POSITIONS: number[][] = [
  /*  1 */ [],
  /*  2 */ [6, 18],
  /*  3 */ [6, 22],
  /*  4 */ [6, 26],
  /*  5 */ [6, 30],
  /*  6 */ [6, 34],
  /*  7 */ [6, 22, 38],
  /*  8 */ [6, 24, 42],
  /*  9 */ [6, 26, 46],
  /* 10 */ [6, 28, 50],
  /* 11 */ [6, 30, 54],
  /* 12 */ [6, 32, 58],
  /* 13 */ [6, 34, 62],
  /* 14 */ [6, 26, 46, 66],
  /* 15 */ [6, 26, 48, 70],
  /* 16 */ [6, 26, 50, 74],
  /* 17 */ [6, 30, 54, 78],
  /* 18 */ [6, 30, 56, 82],
  /* 19 */ [6, 30, 58, 86],
  /* 20 */ [6, 34, 62, 90],
  /* 21 */ [6, 28, 50, 72, 94],
  /* 22 */ [6, 26, 50, 74, 98],
  /* 23 */ [6, 30, 54, 78, 102],
  /* 24 */ [6, 28, 54, 80, 106],
  /* 25 */ [6, 32, 58, 84, 110],
  /* 26 */ [6, 30, 58, 86, 114],
  /* 27 */ [6, 34, 62, 90, 118],
  /* 28 */ [6, 26, 50, 74, 98, 122],
  /* 29 */ [6, 30, 54, 78, 102, 126],
  /* 30 */ [6, 26, 52, 78, 104, 130],
  /* 31 */ [6, 30, 56, 82, 108, 134],
  /* 32 */ [6, 34, 60, 86, 112, 138],
  /* 33 */ [6, 30, 58, 86, 114, 142],
  /* 34 */ [6, 34, 62, 90, 118, 146],
  /* 35 */ [6, 30, 54, 78, 102, 126, 150],
  /* 36 */ [6, 24, 50, 76, 102, 128, 154],
  /* 37 */ [6, 28, 54, 80, 106, 132, 158],
  /* 38 */ [6, 32, 58, 84, 110, 136, 162],
  /* 39 */ [6, 26, 54, 82, 110, 138, 166],
  /* 40 */ [6, 30, 58, 86, 114, 142, 170],
];

function parseRow(row: EcRow): EcBlocks {
  const groups: EcBlockGroup[] = [{ count: row[1], dataCodewords: row[2] }];
  if (row.length === 5) groups.push({ count: row[3], dataCodewords: row[4] });
  return { ecCodewordsPerBlock: row[0], groups };
}

const VERSIONS: VersionInfo[] = ECC_TABLE.map((levels, i) => ({
  version: i + 1,
  size: sizeForVersion(i + 1),
  alignmentPositions: ALIGNMENT_POSITIONS[i]!,
  ecBlocks: {
    L: parseRow(levels[0]),
    M: parseRow(levels[1]),
    Q: parseRow(levels[2]),
    H: parseRow(levels[3]),
  },
}));

export function getVersionInfo(version: number): VersionInfo {
  const info = VERSIONS[version - 1];
  if (info === undefined) throw new RangeError(`invalid QR version ${version}`);
  return info;
}
